import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { getPagePath, trans } from '@spotahome/soyuz/client';
import { Chip } from '@spotahome/ui-library';
import { DSIcon } from '@spotahome/ui-library/base';

import { trackHomeSubmit } from '../agenticTracking';

import './HeroAgenticInput.scss';

// Auto-grow clamps per variant. Home is a tall hero box; compact is the
// in-conversation composer (a couple of lines, smaller cap). Kept in sync
// with the min/max-height in the SCSS.
const HEIGHTS = {
  home: { min: 152, max: 320 },
  compact: { min: 72, max: 160 }
};

const PLACEHOLDER_COUNT = 10;
const PLACEHOLDER_KEYS = Array.from(
  { length: PLACEHOLDER_COUNT },
  (_, i) => `marketplace.agentic.home.input.placeholder.${i}`
);

// Typewriter animation timings. Type/erase budgets are per-phrase totals
// (so longer phrases simply type faster per character); the floor keeps
// very short phrases from strobing.
const TYPE_DURATION_MS = 1500;
const HOLD_DURATION_MS = 5000;
const ERASE_DURATION_MS = 700;
const MIN_CHAR_DELAY_MS = 20;

const charDelay = (totalMs, length) =>
  Math.max(MIN_CHAR_DELAY_MS, totalMs / Math.max(length, 1));

// Cycles through the provided phrases with a type → hold → erase → next
// loop. Pauses when `isPaused` flips true (user has draft text, so the
// placeholder is hidden anyway) and restarts from phrase 0 on resume —
// simpler than tracking resume state, and visually identical since the
// animation is invisible while paused. Respects prefers-reduced-motion
// by skipping the animation and freezing on the first phrase.
const usePlaceholderRotation = (phrases, isPaused) => {
  const [text, setText] = useState('');

  useEffect(() => {
    if (isPaused || phrases.length === 0) return undefined;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setText(phrases[0]);
      return undefined;
    }

    let cancelled = false;
    let timeoutId;

    const schedule = (fn, ms) => {
      timeoutId = setTimeout(() => {
        if (!cancelled) fn();
      }, ms);
    };

    const runPhrase = idx => {
      const phrase = phrases[idx % phrases.length];
      const typeStep = charDelay(TYPE_DURATION_MS, phrase.length);
      const eraseStep = charDelay(ERASE_DURATION_MS, phrase.length);

      const eraseChar = i => {
        setText(phrase.slice(0, i));
        if (i > 0) {
          schedule(() => eraseChar(i - 1), eraseStep);
        } else {
          schedule(() => runPhrase(idx + 1), typeStep);
        }
      };

      const typeChar = i => {
        setText(phrase.slice(0, i));
        if (i < phrase.length) {
          schedule(() => typeChar(i + 1), typeStep);
        } else {
          schedule(() => eraseChar(phrase.length - 1), HOLD_DURATION_MS);
        }
      };

      typeChar(1);
    };

    runPhrase(0);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isPaused, phrases]);

  return text;
};

// "Be inspired" chips, rendered above the popular cities. Each id maps to a
// pair of Loco keys — `.label` (chip text) and `.tag` (the short phrase the
// chip contributes to the composed prompt, e.g. "with my pet") — so PMs can
// tune both without code changes. `icon` is a DSIcon registry name.
const INSPIRATION_CHIPS = [
  { id: 'erasmus', icon: 'perfect_for_students' },
  { id: 'first_job', icon: 'perfect_for_professionals' },
  { id: 'pet', icon: 'pets_allowed' },
  { id: 'short_stay', icon: 'clock' },
  { id: 'metro', icon: 'subway' },
  { id: 'budget', icon: 'euro' }
];

const inspirationLabelKey = id =>
  `marketplace.agentic.home.inspiration.${id}.label`;
const inspirationTagKey = id =>
  `marketplace.agentic.home.inspiration.${id}.tag`;

// Chips wear a pink→periwinkle gradient — subtle when idle, stronger when
// selected (the active state replaces the design-system lilac fill).
const chipClassName = selected =>
  `hero-agentic-input__chip ${
    selected
      ? 'hero-agentic-input__chip--gradient-active'
      : 'hero-agentic-input__chip--gradient'
  }`;

// Shared agentic input, used on two surfaces via `variant`:
//
//   - `home` (default): the homepage hero entry-point. A tall textarea that
//     expands on focus into a panel of "Be inspired" + popular-city chips
//     (selectable toggles that compose the prompt), wears the breathing glow
//     + always-rotating border, and on submit navigates to /agentic-search
//     via a `?q=` param.
//   - `compact`: the in-conversation composer on /agentic-search. No chips,
//     no expand, a couple of lines tall, no glow; the rotating border shows
//     only while the agent is thinking (`isThinking`). On submit it hands the
//     text to `onSubmit` (the chat `send`) and clears, instead of navigating.
export const HeroAgenticInput = ({
  className = '',
  cities = [],
  variant = 'home',
  onSubmit = null,
  isThinking = false,
  placeholder = '',
  sendLabel = ''
}) => {
  const isCompact = variant === 'compact';
  const [draft, setDraft] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  // Active chip selection — inspirations are multi-select (ordered by click),
  // the city is single-select. This is the source of truth for the composed
  // draft (see the recompose effect below).
  const [selectedInspirations, setSelectedInspirations] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const textareaRef = useRef(null);
  // Wraps the whole card so the click-outside listener can tell an
  // in-card chip click (keep open) from an outside click (collapse).
  const containerRef = useRef(null);
  // The suggestions panel — toggled `inert` while collapsed (see below).
  const panelRef = useRef(null);

  // `tracking` ({ source, charLength }) is fired synchronously before the
  // navigation (GA4 agentic_home_submit). sendGA4Event is a dataLayer.push,
  // so the event is queued before the page unloads — same fire-then-navigate
  // contract the classic homecard_click relies on.
  const navigate = useCallback((text, tracking = {}) => {
    if (typeof window === 'undefined') return;
    trackHomeSubmit(tracking);
    const basePath = getPagePath('SearchAgentic');
    window.location.href = `${basePath}?q=${encodeURIComponent(text)}`;
  }, []);

  // Home navigates to /agentic-search?q=… (and fires the home-submit event);
  // compact hands the text to the chat via `onSubmit`, then clears + refocuses
  // for the next message. Never submits while the agent is thinking (compact
  // only; home is always ready). Chips now compose the draft rather than
  // submit, so every home submit is free-text (the old `city_chip` source is
  // gone — clicking a city no longer navigates).
  const submit = useCallback(
    text => {
      if (isThinking) return;
      if (onSubmit) {
        onSubmit(text);
        setDraft('');
        const el = textareaRef.current;
        if (el) el.focus();
      } else {
        navigate(text, { source: 'freetext', charLength: text.length });
      }
    },
    [isThinking, onSubmit, navigate]
  );

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      const text = draft.trim();
      if (!text) return;
      submit(text);
    },
    [draft, submit]
  );

  // Enter submits, Shift+Enter inserts a newline. Mirrors the convention
  // most chat surfaces use, including the in-conversation input the user
  // sees on /agentic-search.
  const handleKeyDown = useCallback(
    e => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      e.preventDefault();
      const text = draft.trim();
      if (!text) return;
      submit(text);
    },
    [draft, submit]
  );

  // Keep the box open and return focus with the caret at the end after a
  // chip toggle. Deferred to the next frame because the textarea is
  // controlled — its DOM value only reflects the recomposed draft after
  // React commits.
  const refocusTextarea = useCallback(() => {
    setIsExpanded(true);
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    requestAnimationFrame(() => {
      const len = el.value.length;
      el.setSelectionRange(len, len);
    });
  }, []);

  // Chips always rebuild: whenever the active selection changes, recompose
  // the draft from scratch — inspiration tags in click order, then the city
  // last. This overwrites manual edits on purpose; free typing still updates
  // the draft but never feeds back into the selection, so the next toggle
  // wins. Capitalise the first character so a lowercase-leading tag (e.g.
  // "with my pet") still reads as a sentence start.
  useEffect(() => {
    if (isCompact) return;
    const parts = selectedInspirations.map(id => trans(inspirationTagKey(id)));
    const city = cities.find(c => c && c.id === selectedCityId);
    if (city) parts.push(city.cityName);
    const text = parts.join(', ');
    setDraft(text.charAt(0).toUpperCase() + text.slice(1));
    // `cities` is stable for the page lifetime; only the selection should
    // retrigger a recompose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInspirations, selectedCityId]);

  const toggleInspiration = useCallback(
    id => {
      setSelectedInspirations(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      );
      refocusTextarea();
    },
    [refocusTextarea]
  );

  // Single-select: re-clicking the active city clears it.
  const selectCity = useCallback(
    id => {
      setSelectedCityId(prev => (prev === id ? null : id));
      refocusTextarea();
    },
    [refocusTextarea]
  );

  // Collapse when the user clicks outside the card. Bound on mousedown (not
  // blur) so clicking an in-card chip — which lives inside containerRef —
  // never collapses the panel, while any outside target reliably does.
  useEffect(() => {
    if (!isExpanded) return undefined;
    const handleOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isExpanded]);

  // While collapsed the panel is kept mounted (so it can animate open), but
  // `aria-hidden` + `pointer-events: none` leave its chip buttons in the tab
  // order — a keyboard user could Tab into an aria-hidden subtree (axe flags
  // this). Mark it `inert` to pull the whole subtree out of the tab order and
  // the a11y tree, without losing the open/close animation (vs display:none).
  useEffect(() => {
    const el = panelRef.current;
    if (el) el.toggleAttribute('inert', !isExpanded);
  }, [isExpanded]);

  // Resize the textarea to fit its content, capped at MAX_HEIGHT_PX (beyond
  // which it falls back to internal scrolling). Reset to `auto` first so
  // the textarea can shrink when content is deleted.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const { min, max } = isCompact ? HEIGHTS.compact : HEIGHTS.home;
    el.style.height = 'auto';
    const fit = Math.max(min, Math.min(el.scrollHeight, max));
    el.style.height = `${fit}px`;
  }, [draft, isCompact]);

  const isSendDisabled = !draft.trim() || (isCompact && isThinking);
  const hasCities = Array.isArray(cities) && cities.length > 0;

  // Locale-resolved phrases for the typewriter (home only). Memoised so the
  // hook's effect dep stays referentially stable across renders.
  const placeholderPhrases = useMemo(
    () => PLACEHOLDER_KEYS.map(key => trans(key)),
    []
  );
  // Compact uses a static placeholder prop and pauses the typewriter.
  const animatedPlaceholder = usePlaceholderRotation(
    placeholderPhrases,
    isCompact || draft.length > 0
  );
  const resolvedPlaceholder = isCompact ? placeholder : animatedPlaceholder;
  const inputAriaLabel = isCompact
    ? placeholder
    : trans('marketplace.agentic.home.input.aria');
  const resolvedSendLabel = isCompact
    ? sendLabel
    : trans('marketplace.agentic.home.input.sendLabel');

  return (
    <form
      ref={containerRef}
      className={`hero-agentic-input${
        isCompact ? ' hero-agentic-input--compact' : ''
      } ${className || ''}`}
      onSubmit={handleSubmit}
    >
      <div
        className={`hero-agentic-input__card${
          isThinking ? ' hero-agentic-input__card--thinking' : ''
        }`}
      >
        <div className="hero-agentic-input__editor">
          <DSIcon
            name="auto_awesome"
            size="sm"
            className="hero-agentic-input__leading-icon"
          />
          <textarea
            ref={textareaRef}
            rows={isCompact ? 2 : 3}
            className="hero-agentic-input__input"
            placeholder={resolvedPlaceholder}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={isCompact ? undefined : () => setIsExpanded(true)}
            aria-label={inputAriaLabel}
          />
          <button
            type="submit"
            className="hero-agentic-input__send-button"
            aria-label={resolvedSendLabel}
            disabled={isSendDisabled}
          >
            <DSIcon name={isCompact ? 'arrow_upward' : 'search'} size="sm" />
          </button>
        </div>
        {!isCompact && (
          <div
            ref={panelRef}
            className={`hero-agentic-input__panel${
              isExpanded ? ' hero-agentic-input__panel--expanded' : ''
            }`}
            aria-hidden={!isExpanded}
          >
            <div className="hero-agentic-input__panel-inner">
              <div className="hero-agentic-input__section">
                <span className="hero-agentic-input__section-label">
                  {trans('marketplace.agentic.home.inspiration.label')}
                </span>
                <div className="hero-agentic-input__chips">
                  {INSPIRATION_CHIPS.map(chip => {
                    const selected = selectedInspirations.includes(chip.id);
                    return (
                      <Chip.Filter
                        key={chip.id}
                        label={trans(inspirationLabelKey(chip.id))}
                        className={chipClassName(selected)}
                        isSpaced
                        isSelected={selected}
                        onClick={() => toggleInspiration(chip.id)}
                        renderLeadingIcon={() => (
                          <DSIcon name={chip.icon} size="s" />
                        )}
                      />
                    );
                  })}
                </div>
              </div>
              {hasCities && (
                <div className="hero-agentic-input__section">
                  <span className="hero-agentic-input__section-label">
                    {trans('marketplace.agentic.home.popular_cities.label')}
                  </span>
                  <div className="hero-agentic-input__chips">
                    {cities.map(city => {
                      const selected = selectedCityId === city.id;
                      return (
                        <Chip.Filter
                          key={city.id}
                          label={city.cityName}
                          className={chipClassName(selected)}
                          isSpaced
                          isSelected={selected}
                          onClick={() => selectCity(city.id)}
                          renderLeadingIcon={() => (
                            <DSIcon name="location_on-1" size="s" />
                          )}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

HeroAgenticInput.propTypes = {
  className: PropTypes.string,
  cities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      cityName: PropTypes.string.isRequired
    })
  ),
  // 'home' (default) or 'compact' (in-conversation composer).
  variant: PropTypes.oneOf(['home', 'compact']),
  // When provided (compact), submit hands the text here instead of
  // navigating to /agentic-search.
  onSubmit: PropTypes.func,
  // Compact: agent is processing — animates the border and disables send.
  isThinking: PropTypes.bool,
  // Compact: static placeholder + aria-label (home uses the typewriter).
  placeholder: PropTypes.string,
  // Compact: send-button aria-label.
  sendLabel: PropTypes.string
};

export default HeroAgenticInput;
